import type { Risk } from '../../types/api';
import { RiskContent } from './RiskDetailCard';

export interface GeospatialInsightCardsListProps {
  /** Risks in the focused map area only (empty array when none). */
  risks: Risk[];
  /** GeoJSON country name when the map has a focused area; null before the user selects or zoom-selects an area. */
  selectedMapArea: string | null;
}

function riskStableKey(risk: Risk, index: number): string {
  const pub = risk.publication_date ?? '';
  const loc = risk.location ?? '';
  const country = risk.country ?? '';
  return `${pub}|${country}|${loc}|${index}`;
}

export function GeospatialInsightCardsList({ risks, selectedMapArea }: GeospatialInsightCardsListProps) {
  if (selectedMapArea === null) {
    return (
      <div className="geospatial-insight-panel" aria-label="Insights">
        <div className="geospatial-insight-panel__scroll geospatial-insight-panel__scroll--empty">
          <div className="risk-detail-card-carousel">
            <div className="risk-detail-card risk-detail-card--filled risk-detail-card--no-area">
              <div className="risk-detail-card__main risk-detail-card__main--no-area">
                <p className="risk-detail-card__no-area-message">
                  Select a country on the map or zoom in until an area is highlighted to see risks and opportunities
                  for that area only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (risks.length === 0) {
    return (
      <div className="geospatial-insight-panel" aria-label="Insights">
        <div className="geospatial-insight-panel__scroll geospatial-insight-panel__scroll--empty">
          <div className="risk-detail-card-carousel">
            <div className="risk-detail-card risk-detail-card--filled risk-detail-card--no-area risk-detail-card--empty-area">
              <div className="risk-detail-card__main risk-detail-card__main--no-area">
                <p className="risk-detail-card__no-area-message">
                  <strong>{selectedMapArea}</strong> has no mapped risks or opportunities in the current data and
                  filters. Try another area or widen filters if you expect items here.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="geospatial-insight-panel" aria-label="Insights">
      <div className="geospatial-insight-panel__scroll">
        {risks.map((risk, index) => {
          const isOpportunity = risk.is_positive === true;
          const typeLabel = isOpportunity ? 'Opportunity' : 'Risk';
          return (
            <article
              key={riskStableKey(risk, index)}
              className={`risk-detail-card risk-detail-card--filled risk-detail-card--list-item risk-detail-card--${isOpportunity ? 'opp' : 'risk'}`}
              aria-label={`${typeLabel}: ${risk.risk_category || risk.location || 'item'}`}
            >
              <RiskContent risk={risk} />
            </article>
          );
        })}
      </div>
    </div>
  );
}
